import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Label } from 'lib/Label';
import { BaseSelectStyleProps } from 'lib/BaseSelect/BaseSelect.types';
import { changeDealData } from 'store/dealManagement/reducer';
import { InputTheme } from 'components/common/types/Input.types';
import AsyncSelect from 'lib/AsyncSelect';
import { SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { Store } from 'components/common/types/Store.types';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { Advertiser, DealStatus } from 'components/common/types/Deal.types';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { getAdvertiser } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectAdvertiserProps extends BaseSelectStyleProps {
  advertisers: Advertiser[];
  onAdvertiserChange: (value: Advertiser | null) => void;
  dataTestId?: string;
  placeholder?: string;
  withLabel?: boolean;
  isRequired?: boolean;
}

export const SelectAdvertiser = ({
  advertisers,
  onAdvertiserChange,
  dataTestId,
  placeholder,
  theme,
  size,
  padding,
  shadow,
  shape,
  withLabel = false,
  isDisabled = false,
  isRequired = false,
}: SelectAdvertiserProps): React.ReactElement => {
  const advertiser = useSelector(getAdvertiser);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);

  const dispatch = useDispatch();
  const { onAssetLookup } = useLoadLookup(0);
  const readOnly = useSelector(getDealFormReadOnly);
  const { isCampaignReadOnly } = useIsReadOnly();
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const { getPendoDataId } = usePendoTracking();

  const onSelect: SelectHandler<Advertiser> = (dropdownName, value: Advertiser | null) => {
    dispatch(changeDealData({ [dropdownName]: value }));
    onAdvertiserChange(value);
  };

  return (
    <>
      {withLabel ? (
        <Label id="advertiser" theme={InputTheme.FLAT_GRAY} label="Advertiser" isRequired={isRequired} />
      ) : null}
      <Tooltip tooltip={advertiser?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <AsyncSelect
          dataTestId={dataTestId}
          name="advertiser"
          placeholder={placeholder}
          selectedValue={advertiser}
          loadData={onAssetLookup}
          onSelect={onSelect}
          defaultOptions={advertisers}
          isDisabled={isDisabled || (readOnly && !isPendingReservation) || isCampaignReadOnly}
          theme={theme}
          size={size}
          padding={padding}
          shadow={shadow}
          shape={shape}
          dataPendoId={getPendoDataId('Advertiser')}
        />
      </Tooltip>
    </>
  );
};
